/* eslint-disable jsx-a11y/media-has-caption */

import React, { useEffect, useState } from "react";

import { makeDelete } from "@culturehq/client";
import { Icon, PlainButton, Thumbnail } from "@culturehq/components";
import { useProfile } from "app/context";
import { ProfileLink } from "app/links";
import FacebookLink from "app/links/FacebookLink";
import { canAdminSocialShare } from "app/permissions";
import { getImageFromStory, Trans } from "app/utils";

import facebook from "images/social/facebook.svg";

import imagesSizes from "constants/imagesSizes";

import Subtitles from "components/common/Subtitles";
import { TimeDiff } from "components/common/Time";
import QuickOptions from "components/display/QuickOptions";
import FeedItemTitle from "components/feed/feed-item/preview/FeedItemTitle";

import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";
import PinUserActivityToggle from "../PinUserActivityToggle";

const PostQuickOptions = ({
  internalShare, userActivity, onUserActivityUpdate, onUserActivityRemove
}) => {
  const { profile } = useProfile();
  const { entity: socialShare } = userActivity;

  const onSocialShareDeleted = async () => {
    if (internalShare) {
      await makeDelete(`/stories/social_share/${socialShare.id}`);
    } else {
      await makeDelete(`/stories/external_social_share/${socialShare.id}`);
    }
    onUserActivityRemove(userActivity.id);
  };

  return (
    <QuickOptions>
      <ul className="dropdown-menu">
        {profile.organizationAdmin && (
          <li>
            <PinUserActivityToggle
              userActivity={userActivity}
              onUserActivityUpdate={onUserActivityUpdate}
            />
          </li>
        )}
        {canAdminSocialShare(profile, socialShare) && (
          <li>
            <ChqModalConfirmDelete
              onAccept={onSocialShareDeleted}
              trigger={onTrigger => (
                <PlainButton onClick={onTrigger} className="link action">
                  <Icon icon="trash-a" />
                  <Trans>Delete</Trans>
                </PlainButton>
              )}
            >
              <Trans>Are you sure you want to delete this social share?</Trans>
            </ChqModalConfirmDelete>
          </li>
        )}
      </ul>
    </QuickOptions>
  );
};

const FacebookCreatorInfo = ({ story, facebookShare }) => {
  const [creator, setCreator] = useState();
  const socialShare = story?.socialShares ? story.socialShares[0] : facebookShare;

  useEffect(
    () => {
      if (story) {
        setCreator(story.creator);
      } else {
        setCreator(facebookShare.creator);
      }
    }, [facebookShare, story]
  );

  if (socialShare?.socialCompany) {
    return (
      <>
        <span className="Linkedin_Creator--Information_Creator">{socialShare.socialCompany.name}</span>
        <Trans>&apos;s story on Facebook!</Trans>
      </>
    );
  }

  if (!story) {
    return (
      <Trans>a story on Facebook!</Trans>
    );
  }

  if (socialShare?.creator?.id === creator?.id) {
    return (
      <Trans>its story on Facebook!</Trans>
    );
  }

  return (
    <>
      <ProfileLink user={creator} />
      <Trans>&apos;s story on Facebook!</Trans>
    </>
  );
};

const FacebookPostPreview = ({
  story, userActivity, facebookShare, onUserActivityRemove, onUserActivityUpdate
}) => {
  const [imageUrl, setImageUrl] = useState();
  const [media, setMedia] = useState();

  useEffect(
    () => {
      if (story) {
        setImageUrl(story.media.mediaType === "image" ? getImageFromStory(imagesSizes.NORMAL, story) : story.media.thumbnail);
        setMedia(story.media);
      } else {
        setImageUrl(facebookShare.media.mediaType === "image" ? facebookShare.media.url : facebookShare.media.thumbnail);
        setMedia(facebookShare.media);
      }
    }, [story, facebookShare]
  );

  const getAccountImage = () => {
    if (facebookShare.socialCompany) {
      return facebookShare.socialCompany.logo;
    }
    return facebookShare?.creator?.avatar?.thumbUrl || story.creator.avatar.thumbUrl;
  };

  const getAccountText = () => {
    if (facebookShare.socialCompany) {
      return facebookShare.socialCompany.name;
    }
    return facebookShare.creator.name;
  };

  return (
    <div className="FeedItem__preview__photo_created Story FeedItem__preview__social">
      <FeedItemTitle
        item={userActivity}
        date={userActivity.bumpedAt}
        user={facebookShare?.creator || story.creator}
      >
        <ProfileLink user={facebookShare?.creator || story.creator} />
        {facebookShare?.creator ? (
          <span>
            {" "}
            <Trans>shared</Trans>
            {" "}
            <FacebookCreatorInfo story={story} facebookShare={facebookShare} />
          </span>
        ) : (
          <span>
            <Trans>&apos;s story has been approved to share on social!</Trans>
          </span>
        )}
        <PostQuickOptions
          internalShare={!!story}
          userActivity={userActivity}
          onUserActivityUpdate={onUserActivityUpdate}
          onUserActivityRemove={onUserActivityRemove}
        />
      </FeedItemTitle>
      <FacebookLink facebookId={facebookShare.externalId}>
        <div className="FeedItem__preview__Social__Container FeedItem__preview__Facebook__Container">
          <div className="FeedItem__preview__Social__Header">
            <div className="FeedItem__preview__Social__User">
              <div className="User__Info-Container">
                <Thumbnail className="Thumbnail" image={getAccountImage()} title={facebookShare?.creator?.name} />
                <div>
                  <p className="Preview-User__Info Preview-User__Info-Name">{getAccountText()}</p>
                  <p className="FeedItem__preview__header__date">
                    <TimeDiff time={facebookShare.externalCreateTime * 1000} format="%B %e at %l:%M %p" />
                  </p>
                </div>
              </div>
              <img className="social-icon" src={facebook} alt="Facebook" />
            </div>
            <p className="message">{facebookShare.message}</p>
          </div>
          <div className="gallery-lightbox__photo">
            <div className="gallery-lightbox__image-container" style={{ backgroundImage: `url(${imageUrl})` }} />
            {media?.mediaType === "image" && (
              <img
                className="gallery-lightbox__photo--image"
                src={imageUrl}
                alt={story?.imageAlt || story?.question?.question || "Facebook image"}
              />
            )}
            {media?.mediaType === "video" && (
              <>
                <video
                  controls={false}
                  className="gallery-lightbox__main-image gallery-lightbox__main-video"
                  poster={`${media.thumbnail}?not-from-cache-please`}
                  src={media.url}
                  disablePictureInPicture
                  crossOrigin="anonymous"
                  controlsList="nodownload"
                >
                  <Subtitles media={media} />
                </video>
                <Icon className="PlayIcon" icon="play-no-border" />
              </>
            )}
          </div>
          <div className="FeedItem__preview__Social__Footer">
            {(facebookShare.reactions > 0 || facebookShare.comments > 0) && (
              <>
                <div className="Interactions-Container">
                  <span className="Reactions__Count">
                    {facebookShare.reactions}
                    {" "}
                    {facebookShare.reactions === 1 ? "person has" : "people have"}
                    {" "}
                    <Trans>reacted to this post</Trans>
                  </span>
                  <span className="Comments__Count">{`${facebookShare.comments} Comment${facebookShare.comments === 1 ? "" : "s"}`}</span>
                </div>
                <hr />
              </>
            )}
            <div className="Facebook-Buttons">
              <PlainButton>
                <svg className="facebook-icon" width="22px" height="22px" viewBox="0 0 494 489">
                  <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                    <g transform="translate(11.000000, 0.000000)">
                      <path fill="#888" d="M392.333,469.333 L195,469.333 C169.135,469.127 144.031,460.55 123.448,444.885 C121.449,442.881 120.328,440.164 120.331747,437.333 L120.331747,245.333 C120.331747,241.296 122.612,237.605 126.221,235.797 L144.205,226.794 L205.666,93.674 L205.666,21.333 C205.659,18.091 207.127,15.021 209.655,12.992 C220.881,4.742 234.402,0.201 248.332,0 C273.015,0 321.640591,39.4264079 321.640591,85.6984079 C321.640591,115.723794 320.832371,143.175 311.124371,170.666 L429.665,170.666 C459.12,170.666 483.000091,194.544 483.000091,223.999 C483.033,237.236 478.082,250.001 469.131,259.754 C487.697,278.445 487.595,308.648 468.904,327.214 C465.313,330.781 461.176,333.754 456.651,336.021 C459.971,342.641 461.688,349.949 461.664,357.354 C461.644,375.41 451.452,391.915 435.317,400.021 C438.637,406.641 440.354,413.949 440.33,421.354 C440.286,447.841 418.82,469.298 392.333,469.333 Z" />
                      <path fill="#FFFFFF" d="M145.667,427.909712 C161.415991,437.288745 179.320683,442.484764 197.666069,443 L390.0635,443 C404.418258,442.988356 416.051822,431.410597 416.063522,417.124697 C416.064497,410.014252 413.086882,403.225951 407.847293,398.390888 C403.663617,394.47471 403.461794,387.924135 407.396848,383.760528 C409.082603,381.976124 411.350427,380.844741 413.795699,380.568202 C427.949608,379.079744 438.211364,366.453077 436.714756,352.367061 C436.061514,346.215285 433.196022,340.505971 428.646725,336.290936 C424.463049,332.374758 424.261227,325.824184 428.196281,321.660576 C429.882036,319.876173 432.14986,318.744789 434.595131,318.46825 C448.750016,316.983673 459.014696,304.360888 457.522964,290.274872 C456.600624,281.561473 451.272312,273.919298 443.393428,270.009912 C438.289363,267.388129 436.286736,261.146114 438.921156,256.065562 C439.78792,254.392744 441.09733,252.987733 442.707036,252.000926 C457.330891,242.931422 461.798289,223.781349 452.68507,209.227644 C447.035986,200.205685 437.142776,194.687522 426.461776,194.600194 L291.264977,194.600194 C285.521319,194.597283 279.927409,193.758767 279.184435,188.128318 C278.044702,179.491133 280.266474,176.573694 280.716918,175.398646 C291.676766,146.684211 293.499575,120.67972 295.879522,90.0557108 C295.879522,56.1491373 261.063729,29 249.665137,29 C242.370272,29.1174077 235.220681,31.0454172 228.865705,34.6093722 L228.865705,101.449297 C228.863755,102.949398 228.538109,104.432035 227.909241,105.796293 L165.509969,240.346511 C164.51938,242.473435 162.83265,244.203501 160.725699,245.252408 L145.667,252.746125 L145.667,427.909712 Z" />
                      <rect stroke="#888" strokeWidth="22" fill="#FFFFFF" x="0" y="200" width="130" height="278" rx="20" />
                    </g>
                  </g>
                </svg>
                <Trans>Like</Trans>
              </PlainButton>
              <PlainButton>
                <svg className="facebook-icon" width="22px" height="22px" viewBox="0 0 454 434">
                  <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                    <g itransform="translate(0.000000, 0.000000)" fillRule="nonzero">
                      <path fill="#888" d="M385.9,0 L68.1,0 C30.5068194,0.0429688 0.0406413233,32.253906 0,72 L0,280 C0.0406413233,319.746094 30.5068194,351.957031 68.1,352 L89.1337074,352 C85.8444093,391.440703 84.1997603,413.486538 84.1997603,418.137504 C84.1997603,425.113953 86.7986814,428.527344 89.1337074,430.375 C91.4687335,432.222656 97.4563404,435.744319 109.477867,430.375 L228.869498,352 L385.9,352 C423.493181,351.957031 453.959358,319.746094 454,280 L454,72 C453.959358,32.253906 423.493181,0.0429688 385.9,0 Z" />
                      <path fill="#FFFFFF" d="M426,272.09888 C425.968846,300.799645 403.769827,324.056913 376.375,324.089552 L227.5,324.089552 C226.347299,324.089552 225.215367,324.383304 224.197667,324.945425 L111.710917,393.895059 L116.024157,332.535197 C116.327893,328.479758 116.024157,325.888483 114.620268,324.945425 C113.21638,324.002367 109.111006,324.089552 106.982143,324.089552 L78.625,324.089552 C51.2301727,324.056913 29.0311539,300.799645 29,272.09888 L29,78.9906715 C29.0311539,50.2899065 51.2301727,27.032639 78.625,27 L376.375,27 C403.769827,27.032639 425.968846,50.2899065 426,78.9906715 L426,272.09888 Z" />
                    </g>
                  </g>
                </svg>
                <Trans>Comment</Trans>
              </PlainButton>
              <PlainButton>
                <svg className="facebook-icon" width="22px" height="22px" viewBox="0 0 473 400">
                  <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                    <g transform="translate(0.666583, 0.000000)" fillRule="nonzero">
                      <path fill="#888" d="M18.3958137,399.14673 C13.6053324,399.704122 9.66528395,397.969601 8.48595062,397.701051 C2.61542458,394.092194 0,390.756955 0,383.640552 C0,217.324968 80.7013252,125.08052 240.333417,125.08052 L240.333417,15.3437144 C240.333417,9.24379805 243.97195,3.73085476 249.594083,1.28705178 C255.196883,-1.14140549 261.76635,-0.0902249332 266.298083,4.07613304 L467.36475,188.224552 C470.535417,191.11338 472.333417,195.206846 472.333417,199.492133 C472.333417,203.777421 470.535417,207.870886 467.36475,210.763551 L266.298083,394.91197 C261.781817,399.078328 255.21235,400.156364 249.594083,397.701051 C243.97195,395.253412 240.333417,389.740469 240.333417,383.640552 L240.333417,276.400953 C93.3846165,279.8921 56.3219827,354.166698 32.0727809,389.514767 C28.6619766,394.486698 22.7563865,398.519837 18.3958137,399.14673 Z" />
                      <path fill="#FFFFFF" d="M247.770529,250.780577 C257.121113,250.780577 264.687008,258.284038 264.687008,267.55746 L264.687008,363.449928 L442.741417,200.449928 L264.687008,37.4499281 L264.687008,133.342397 C264.687008,142.615819 257.121113,150.119279 247.770529,150.119279 C84.7103283,150.119279 32.2259845,243.14836 26.1422186,347.108873 C61.1804774,300.43139 107.499078,250.780577 247.770529,250.780577 Z" />
                    </g>
                  </g>
                </svg>
                <Trans>Share</Trans>
              </PlainButton>
            </div>
          </div>
        </div>
      </FacebookLink>
    </div>
  );
};

export default FacebookPostPreview;
